'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePageVisibility } from './usePageVisibility';

interface UseHeartbeatOptions {
  interval: number; // milliseconds
  enabled?: boolean;
}

export const useHeartbeat = (callback: () => void, options: UseHeartbeatOptions) => {
  const { enabled = true, interval } = options;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisible = usePageVisibility();

  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    if (!enabled) return;
    stopHeartbeat();
    intervalRef.current = setInterval(() => {
      callback();
    }, interval);
  }, [callback, enabled, interval, stopHeartbeat]);

  useEffect(() => {
    if (isVisible && enabled) {
      startHeartbeat();
    } else {
      stopHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [isVisible, enabled, startHeartbeat, stopHeartbeat]);

  return {
    isActive: isVisible && enabled && intervalRef.current !== null,
    start: startHeartbeat,
    stop: stopHeartbeat,
  };
};
